import type { Actions } from "@/server/types";

// API client that provides full TypeScript type safety
// Usage: api.listInstances() or api.registerInstance({ name: "test" })

// Awaited flattens actions that are already async, so callers always see
// a single Promise instead of Promise<Promise<T>>
type ApiClient = {
  [K in keyof Actions]: Actions[K] extends () => infer R
    ? () => Promise<Awaited<R>>
    : Actions[K] extends (params: infer P) => infer R
      ? (params: P) => Promise<Awaited<R>>
      : never;
};

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

async function rpcCall(action: string, params?: unknown): Promise<unknown> {
  const res = await fetch(`${API_URL}/api/rpc`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ action, params }),
  });

  // Handle 401 - redirect to login
  if (res.status === 401) {
    // Only redirect in browser environment
    if (typeof window !== "undefined") {
      // Keep the query string so the post-login redirect lands on the same view
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    }
    throw new Error("Unauthorized");
  }

  const data = await res.json();

  if (data.error) {
    throw new Error(data.error);
  }

  return data.result;
}

// Proxy that intercepts method calls and converts them to RPC calls
export const api = new Proxy({} as ApiClient, {
  get(_, action: string) {
    return (params?: unknown) => rpcCall(action, params);
  },
});
